import { useEffect, useRef } from 'react';

/**
 * creates div element with specified id
 */
function createRootElement(id: string) {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  return rootContainer;
}

/**
 * inserts element to the DOM
 */
function addRootElement(rootElem: Element) {
  document.body.insertBefore(
    rootElem,
    document.body.lastElementChild?.nextElementSibling || null,
  );
}

/**
 * creates and returns node (div) in body with specified id
 * and destroys it on component unmount
 *
 * It doesn't create React Portal (https://reactjs.org/docs/portals.html)
 * it only creates a container for it
 *
 * Example of usage:
 * const target = usePortal('container');
 * return ReactDOM.createPortal(<SomeComponent />, target);
 */
export function usePortal(id: string) {
  const rootElemRef = useRef<Element | null>(null);

  useEffect(
    function setupElement() {
      const existingParent = document.querySelector(`#${id}`);
      const parentElem = existingParent || createRootElement(id);

      if (!existingParent) addRootElement(parentElem);

      if (rootElemRef.current) parentElem.appendChild(rootElemRef.current);

      return function removeElement() {
        if (rootElemRef.current) rootElemRef.current.remove();

        if (!parentElem.childElementCount) parentElem.remove();
      };
    },
    [id],
  );

  function getRootElem() {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement('div');
    }
    return rootElemRef.current;
  }

  return getRootElem();
}
